<script lang="ts">
  import { z } from 'zod'
  import { Eye, EyeClosed, Link2Off } from '@lucide/svelte'
  import IconButton from '../icon-button/IconButton.svelte'
  import ContextMenu from '../context-menu/ContextMenu.svelte'
  import ContextMenuTrigger from '../context-menu/ContextMenuTrigger.svelte'
  import { setCanvasSelection } from '../../stores/canvas-selection'
  import { validatedForm } from '../../hooks/forms'
  import useGroupQueries from '../../routes/project/hooks/useGroupQueries'
  import type { MouseEventHandler } from 'svelte/elements'
  import useCanvasSelection from '../../hooks/canvas-selection'
  import { ThreeItemKeys } from '../../constants/three'
  import FileContextMenuContent from '../file-context-menu-content/FileContextMenuContent.svelte'
  import RenameForm from '../printer-pane/components/RenameForm.svelte'
  import Color from '../color-picker/Color.svelte'
  import usePrintersQueries from '../../routes/project/hooks/usePrintersQueries'
  import { queryReactiveAccessor } from '../../helpers/tanstack-query'

  const { id } = $props<{ id: number }>()

  const { object, multiSelection, upsert } = useCanvasSelection()

  const {
    focusCamera,
    hideMutation,
    renameMutation,
    showMutation,
    useGroup,
    useGroupFiles,
  } = useGroupQueries()

  const { usePrinters } = usePrintersQueries()
  const printersData = usePrinters()

  /* svelte-ignore state_referenced_locally */
  const groupQuery = useGroup(id)
  /* svelte-ignore state_referenced_locally */
  const groupFilesQuery = useGroupFiles(id)

  let group = $derived.by(queryReactiveAccessor(groupQuery))
  let file = $derived.by(
    queryReactiveAccessor(groupFilesQuery, data => data?.[0]),
  )

  // Read straight off the query (not off `file`, a stable object reference):
  // mutating file.userData in place wouldn't otherwise invalidate this derived.
  let assignedPrinterId = $derived.by(
    queryReactiveAccessor(
      groupFilesQuery,
      data => data?.[0]?.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID],
    ),
  )
  let selectedPrinter = $derived(
    printersData.data?.find(
      item => item.userData[ThreeItemKeys.PRINTER_ID] == assignedPrinterId,
    ),
  )

  let editing = $state(false)

  let selected = $derived(
    $object?.uuid === group?.uuid
      || $multiSelection.some(item => item.uuid === group?.uuid),
  )

  const renameSchema = z.object({
    name: z.string(),
  })

  const { setValue } = validatedForm({
    zodSchema: renameSchema,
    submitFunction: values =>
      renameMutation.mutateAsync({ groupId: id, name: values.name }),
    onSuccess: () => {
      editing = false
    },
  })

  $effect(() => {
    if (editing && group) {
      setValue('name', group.name)
      setCanvasSelection(group)
    }
  })

  const addToSelection: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!group) return
    upsert(group, event && (event.ctrlKey || event.metaKey))
  }

  function toggleHidden() {
    if (!group) return
    if (group.visible) {
      hideMutation.mutate(id)
    }
    else {
      showMutation.mutate(id)
    }
  }

  async function handleRename(name: string): Promise<void> {
    await renameMutation.mutateAsync({ groupId: id, name })
  }
</script>

{#if group && file}
  <ContextMenu>
    <ContextMenuTrigger>
      <div class={['container', { selected, hidden: !group.visible }]}>
        {#if editing}
          <RenameForm
            name={`group-${group.id}`}
            defaultValue={group.name}
            onRename={handleRename}
            onSuccess={() => {
              editing = false
            }}
          />
        {:else}
          <button
            class="name"
            onclick={addToSelection}
            ondblclick={(e) => {
              addToSelection(e)
              focusCamera(id)
            }}
          >
            <div class="color-indicator">
              {#if selectedPrinter}
                <Color
                  color={selectedPrinter?.userData[ThreeItemKeys.PRINTER_COLOR]}
                />
              {:else}
                <Link2Off
                  color="var(--right-bar-muted-foreground)"
                  size=".75rem"
                />
              {/if}
            </div>
            {group.name}
          </button>
        {/if}
        <IconButton onclick={toggleHidden}>
          {#if group.visible}
            <Eye />
          {:else}
            <EyeClosed />
          {/if}
        </IconButton>
      </div>
    </ContextMenuTrigger>
    <FileContextMenuContent
      {assignedPrinterId}
      fileId={file.uuid}
      onRename={() => {
        editing = true
      }}
    />
  </ContextMenu>
{/if}

<style lang="scss">
  .container {
    background-color: var(--printer-pane-background);
    display: flex;
    flex-direction: row;
    gap: 0.25rem;
    align-items: center;
    justify-content: space-between;
    padding: 0 0.5rem 0 1rem;
    min-height: 3rem;

    &:hover,
    &.selected {
      background-color: var(--printer-pane-active-background);
    }

    &.hidden {
      .name {
        opacity: 0.3;
      }
    }

    .name {
      align-items: center;
      background-color: transparent;
      border: none;
      color: currentColor;
      cursor: pointer;
      display: flex;
      flex-direction: row;
      flex: 1;
      gap: 0.5rem;
      outline: none;
      overflow: hidden;
      padding: 0;
      text-align: start;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-height: 2rem;

      .color-indicator {
        align-items: center;
        display: flex;
        height: 1rem;
        justify-content: center;
        width: 1rem;
      }

      &:focus-visible {
        outline: 1px solid var(--stroke);
        outline-offset: -1px;
      }
    }
  }
</style>
